import { Ship } from 'lucide-react'
import { defineModule } from '@/shell/module-system'
import { PacketsListPage } from './pages/PacketsListPage'
import { PacketDetailPage } from './pages/PacketDetailPage'

/**
 * Export Documentation Packet — 2026-09.
 *
 * SPI exports products to Brazil through a trading intermediary
 * (Sertrading) under a legal regime that requires every export document
 * to separately label the Ordering Party (the real SPI customer) from
 * the Importer/Buyer/Consignee/Notify Party (always the intermediary)
 * and the Freight Forwarder. Built after a real shipment (PO M16526) was
 * delayed by those parties getting mixed up in the paperwork.
 *
 * Backend generates ~10 documents per qualifying outbound shipment and
 * walks each one through a review/approval pipeline: ops reviews the
 * auto-generated drafts, sends them to the intermediary through their
 * own channel (no automated email in v1), and once the intermediary
 * approves, ops unlocks "Generate Finals".
 *
 * Sits in the `logistics` nav section alongside Logistics,
 * PO Ship Confirmation, and Material Pickup — a deliberate placement
 * decision, not under `regulatory`, even though the pipeline is
 * compliance-shaped.
 *
 * Permissions mirror `app/rbac.py`'s `export_docs` page key exactly:
 * admin / operations / operations_logistics / regulatory. The
 * compliance-sensitive actions (mark sent / approved / rejected /
 * generate finals) share the same role gate server-side and are
 * deliberately NOT exposed as CARLA tools — human-only per the plan's
 * "no automated Sertrading email in v1" invariant.
 */
export default defineModule({
  id: 'export-docs',
  name: 'Export Documentation Packet',
  shortName: 'Export Docs',
  icon: Ship,
  navSection: 'logistics',
  navOrder: 40,
  urlNamespace: '/export-docs',
  routes: [
    { path: '/', element: PacketsListPage, label: 'Export Docs' },
    { path: '/$packetId', element: PacketDetailPage, label: 'Packet' },
  ],
  permissions: ['admin', 'operations', 'operations_logistics', 'purchasing', 'regulatory'],
  densityDefault: 'comfortable',
  mobileStrategy: 'desktop-only',

  keyboardShortcut: 'g e d',

  commandPaletteEntries: [
    {
      label: 'Go to Export Docs',
      keywords: [
        'export',
        'documentation',
        'packet',
        'sertrading',
        'brazil',
        'intermediary',
        'proforma',
        'packing list',
        'coo',
        'dangerous goods',
      ],
      path: '/',
      group: 'modules',
      shortcut: 'g e d',
    },
  ],
})
